import * as React from "react";

import {
  ACTIVITY_PAGE_SIZE,
  getPollResults,
  listRollingAppointments,
  listRollingLinkEvents,
  listPageBookings,
  listPageEvents,
  listPollEvents,
} from "@/api/client";
import { BookingEventView, RollingLinkEvent, PollEvent, PollResults } from "@/api/types";

import { appointmentToRow, BookingRow, bookingToRow, sortByBookedAtDesc } from "./BookingRows";

/**
 * Per-link activity (who booked + the event journal), fetched on demand and cached.
 *
 * <p>This lives ABOVE {@link AllLinksView}, in the page, on purpose: "All bookings" is unmounted
 * every time you step into a builder, so a cache held inside it would be thrown away on each
 * round trip.</p>
 */

export type LinkKind = "adhoc" | "rolling" | "poll";

export type LinkJournal =
  | { kind: "adhoc"; events: BookingEventView[] }
  | { kind: "rolling"; events: RollingLinkEvent[] }
  | { kind: "poll"; events: PollEvent[] };

export interface LinkActivity {
  rows: BookingRow[];
  journal: LinkJournal | null;
  loading: boolean;
  /** The bookings call failed. The journal failing on its own is silent, it's secondary. */
  error: boolean;
  /**
   * The journal came back 402: the audit log is a Basic feature and this host is on Free. Distinct
   * from `journal === null`, which is any other journal failure and stays silent — this one has
   * something to say, so the History tab shows an upgrade prompt instead of an empty list.
   */
  journalLocked: boolean;
  /** Pages already fetched. The next "load more" asks for this one. */
  page: number;
  /**
   * The last page came back full, so there is probably another.
   *
   * <p>"Probably" is honest: a list whose length is an exact multiple of the page size offers one
   * more page and then returns nothing. That costs a single empty request the first time someone
   * asks, which is cheaper than a count endpoint on five lists.</p>
   *
   * <p>Always false for a poll, whatever its length. See {@link hasMorePages}.</p>
   */
  hasMore: boolean;
  /** A "load more" is in flight, so the button can say so without blanking what is on screen. */
  loadingMore: boolean;
}

export const activityKey = (kind: LinkKind, id: string) => `${kind}:${id}`;

/**
 * Whether a link's "who" list can have another page.
 *
 * <p>Two rules, and the second one is the trap. A full page means there is probably another, which
 * is a guess that costs at most one empty request. But **a poll is never paged at all**: its
 * respondents come from `getPollResults`, an aggregate that returns every one of them in a single
 * shape and takes no page parameter. Asking it for "page 1" hands back the same people again, and
 * the hook appends rather than replaces, so every respondent would appear twice. A poll with 25 or
 * more answers is exactly the case that would have hit it.</p>
 */
export function hasMorePages(kind: LinkKind, rowsInPage: number): boolean {
  if (kind === "poll") return false;
  return rowsInPage >= ACTIVITY_PAGE_SIZE;
}

/**
 * A newly fetched page merged into the rows already on screen.
 *
 * <p>The sort runs over the accumulated list rather than over the page, because sorting a page in
 * isolation interleaves it wrongly with what is already there.</p>
 */
export function mergePages(prior: BookingRow[], incoming: BookingRow[]): BookingRow[] {
  return sortByBookedAtDesc([...prior, ...incoming]);
}

/** True when a rejected fetch was the entitlement gate refusing, rather than a real failure. */
function isPaymentRequired(reason: unknown): boolean {
  return (
    typeof reason === "object" &&
    reason !== null &&
    "status" in reason &&
    (reason as { status: unknown }).status === 402
  );
}

/**
 * A poll's respondents as booking rows: who answered and when.
 *
 * <p>{@code start} is deliberately empty. A respondent has not taken a slot — that is the whole
 * point of a poll — and until the host confirms one there is no time to put here. The winning time,
 * once there is one, belongs on the poll's own detail view, not repeated down every row.</p>
 */
function respondentRows(results: PollResults): BookingRow[] {
  return results.respondents.map((r) => ({
    id: r.id,
    start: "",
    createdAt: r.responded_at,
    inviteeName: r.name,
    inviteeEmail: r.email,
    cancelled: false,
    bounced: false,
    meetingUrl: null,
  }));
}

export function useLinkActivity() {
  const [activity, setActivity] = React.useState<Record<string, LinkActivity>>({});
  // In-flight keys, so an expand/collapse/expand can't fire the same pair of calls twice.
  const inFlight = React.useRef<Set<string>>(new Set());
  // Read the cache without putting it in load()'s deps, which would make load() a new function
  // on every fetch and re-trigger the effect that calls it.
  const cache = React.useRef<Record<string, LinkActivity>>({});
  cache.current = activity;

  /**
   * Fetch one page for a link and merge it into the cache.
   *
   * <p>`page 0` is the first open. Anything above it is a "load more": the rows are appended to
   * what is already there and the journal is left alone, because the journal has its own tab and
   * its own page counter would only confuse this one.</p>
   */
  const fetchPage = React.useCallback((kind: LinkKind, id: string, page: number) => {
    const key = activityKey(kind, id);
    const cached = cache.current[key];
    if (inFlight.current.has(key)) return;
    if (page === 0 && cached && !cached.error) return;

    inFlight.current.add(key);
    setActivity((prev) => {
      const prior = prev[key];
      return {
        ...prev,
        [key]: page === 0
          ? { rows: [], journal: null, loading: true, error: false, journalLocked: false,
              page: 0, hasMore: false, loadingMore: false }
          : { ...prior, loadingMore: true },
      };
    });

    const finish = (
      rows: BookingRow[],
      journal: LinkJournal | null,
      error: boolean,
      journalLocked: boolean,
    ) => {
      inFlight.current.delete(key);
      setActivity((prev) => {
        const prior = prev[key];
        // A full page means there is probably another; a short one is the end of the list. A poll
        // is never paged, so it never claims one.
        const hasMore = hasMorePages(kind, rows.length);
        if (page === 0) {
          return {
            ...prev,
            [key]: { rows, journal, loading: false, error, journalLocked,
                     page: 0, hasMore, loadingMore: false },
          };
        }
        const merged = mergePages(prior?.rows ?? [], rows);
        return {
          ...prev,
          [key]: {
            ...prior,
            rows: merged,
            // A failed "load more" must not blank the rows already being read.
            error: prior?.error ?? false,
            page,
            hasMore: error ? false : hasMore,
            loadingMore: false,
          },
        };
      });
    };

    const locked = (e: PromiseSettledResult<unknown>) =>
      e.status === "rejected" && isPaymentRequired(e.reason);

    // The journal is only fetched with the first page. It is a separate tab with its own list,
    // and paging both from one button would advance a list the user cannot see.
    const journalCall = <T,>(call: Promise<T>): Promise<T> | Promise<null> =>
      page === 0 ? call : Promise.resolve(null);

    // allSettled, not all: the journal is secondary, and a failure there must not blank the
    // bookings list (the same posture BookingPageDetail and RollingLinkBuilder already take).
    if (kind === "adhoc") {
      void Promise.allSettled([
        listPageBookings(id, page, ACTIVITY_PAGE_SIZE),
        journalCall(listPageEvents(id)),
      ]).then(([b, e]) =>
        finish(
          b.status === "fulfilled" ? sortByBookedAtDesc(b.value.map(bookingToRow)) : [],
          e.status === "fulfilled" && e.value ? { kind: "adhoc", events: e.value } : null,
          b.status === "rejected",
          locked(e),
        ),
      );
    } else if (kind === "poll") {
      // A poll has respondents rather than bookings, so its "who" list is everyone who answered.
      // getPollResults is an aggregate and is not paged, so a poll has no second page of people.
      void Promise.allSettled([getPollResults(id), journalCall(listPollEvents(id))]).then(([r, e]) =>
        finish(
          r.status === "fulfilled" ? sortByBookedAtDesc(respondentRows(r.value)) : [],
          e.status === "fulfilled" && e.value ? { kind: "poll", events: e.value } : null,
          r.status === "rejected",
          locked(e),
        ),
      );
    } else {
      void Promise.allSettled([
        listRollingAppointments(id, page, ACTIVITY_PAGE_SIZE),
        journalCall(listRollingLinkEvents(id)),
      ]).then(([a, e]) =>
        finish(
          a.status === "fulfilled" ? sortByBookedAtDesc(a.value.map(appointmentToRow)) : [],
          e.status === "fulfilled" && e.value ? { kind: "rolling", events: e.value } : null,
          a.status === "rejected",
          locked(e),
        ),
      );
    }
  }, []);

  const load = React.useCallback(
    (kind: LinkKind, id: string) => fetchPage(kind, id, 0),
    [fetchPage],
  );

  /** The next page, appended. No-op when the list has already ended. */
  const loadMore = React.useCallback(
    (kind: LinkKind, id: string) => {
      const cur = cache.current[activityKey(kind, id)];
      if (!cur || cur.loading || cur.loadingMore || !cur.hasMore) return;
      fetchPage(kind, id, cur.page + 1);
    },
    [fetchPage],
  );

  /** Drop a cached entry so the next expand refetches (after a cancel, expire, archive…). */
  const invalidate = React.useCallback((kind: LinkKind, id: string) => {
    const key = activityKey(kind, id);
    setActivity((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return { activity, load, loadMore, invalidate };
}
